// Rush Hour puzzle — pure logic, no React

export const GRID_SIZE = 6;
export const KEY_ROW = 3; // 3rd from bottom (0-indexed from top)

// Build 6x6 grid: cell value = piece id or null
export function buildGrid(pieces) {
  const grid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null));
  for (const p of pieces) {
    for (let i = 0; i < p.length; i++) {
      const r = p.orientation === 'v' ? p.row + i : p.row;
      const c = p.orientation === 'h' ? p.col + i : p.col;
      if (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) grid[r][c] = p.id;
    }
  }
  return grid;
}

// Win = all cells to the right of the key on KEY_ROW are empty (key can exit)
export function isWon(pieces) {
  const key = pieces.find(p => p.isKey);
  if (!key) return false;
  const grid = buildGrid(pieces);
  for (let c = key.col + key.length; c < GRID_SIZE; c++) {
    if (grid[key.row][c] !== null) return false;
  }
  return true;
}

export function encodeState(pieces) {
  return pieces.map(p => `${p.id}:${p.row}:${p.col}`).sort().join('|');
}

export function applyMove(pieces, move) {
  return pieces.map(p =>
    p.id === move.pieceId ? { ...p, row: p.row + move.dr, col: p.col + move.dc } : p
  );
}

// All valid single-axis slides of every possible distance
export function getValidMoves(pieces) {
  const grid = buildGrid(pieces);
  const moves = [];
  for (const piece of pieces) {
    if (piece.locked) continue;
    if (piece.orientation === 'h') {
      for (let d = 1; piece.col - d >= 0; d++) {
        if (grid[piece.row][piece.col - d] !== null) break;
        moves.push({ pieceId: piece.id, dr: 0, dc: -d });
      }
      for (let d = 1; piece.col + piece.length - 1 + d < GRID_SIZE; d++) {
        if (grid[piece.row][piece.col + piece.length - 1 + d] !== null) break;
        moves.push({ pieceId: piece.id, dr: 0, dc: d });
      }
    } else {
      for (let d = 1; piece.row - d >= 0; d++) {
        if (grid[piece.row - d][piece.col] !== null) break;
        moves.push({ pieceId: piece.id, dr: -d, dc: 0 });
      }
      for (let d = 1; piece.row + piece.length - 1 + d < GRID_SIZE; d++) {
        if (grid[piece.row + piece.length - 1 + d][piece.col] !== null) break;
        moves.push({ pieceId: piece.id, dr: d, dc: 0 });
      }
    }
  }
  return moves;
}

// BFS — returns shortest move sequence or null
export function solvePuzzle(initialPieces) {
  if (isWon(initialPieces)) return [];
  const queue = [{ pieces: initialPieces, moves: [] }];
  const visited = new Set([encodeState(initialPieces)]);
  while (queue.length > 0) {
    const { pieces, moves } = queue.shift();
    for (const move of getValidMoves(pieces)) {
      const next = applyMove(pieces, move);
      const key = encodeState(next);
      if (!visited.has(key)) {
        visited.add(key);
        const nm = [...moves, move];
        if (isWon(next)) return nm;
        queue.push({ pieces: next, moves: nm });
      }
    }
  }
  return null;
}

export function formatMove(move, pieces) {
  const piece = pieces.find(p => p.id === move.pieceId);
  const label = piece?.isKey ? 'Key' : `Block ${move.pieceId}`;
  if (move.dc < 0) return `${label} ← ${Math.abs(move.dc)}`;
  if (move.dc > 0) return `${label} → ${move.dc}`;
  if (move.dr < 0) return `${label} ↑ ${Math.abs(move.dr)}`;
  return `${label} ↓ ${move.dr}`;
}

export const PIECE_COLORS = {
  key: '#dc2626',
  A: '#2563eb', B: '#16a34a', C: '#d97706',
  D: '#7c3aed', E: '#0891b2', F: '#db2777',
  G: '#65a30d', H: '#ea580c',
  I: '#0e7490', J: '#92400e', K: '#5b21b6', L: '#065f46',
  M: '#9d174d', N: '#1d4ed8', O: '#a16207', P: '#166534',
  Q: '#7e22ce', R: '#0f766e', S: '#b45309', T: '#be185d',
};

export function getPieceColor(piece) {
  if (piece.locked) return '#374151';
  return PIECE_COLORS[piece.id] || '#6b7280';
}

const GEN_BLOCK_IDS = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N'];

function tryPlacePieceOnGrid(id, grid) {
  for (let attempt = 0; attempt < 60; attempt++) {
    const orientation = Math.random() < 0.5 ? 'h' : 'v';
    const r = Math.random();
    const length = r < 0.05 ? 1 : r < 0.35 ? 3 : 2;
    const maxRow = orientation === 'v' ? GRID_SIZE - length : GRID_SIZE - 1;
    const maxCol = orientation === 'h' ? GRID_SIZE - length : GRID_SIZE - 1;
    const row = Math.floor(Math.random() * (maxRow + 1));
    const col = Math.floor(Math.random() * (maxCol + 1));
    let fits = true;
    for (let i = 0; i < length; i++) {
      const r = orientation === 'v' ? row + i : row;
      const c = orientation === 'h' ? col + i : col;
      if (grid[r][c] !== null) { fits = false; break; }
    }
    if (fits) {
      for (let i = 0; i < length; i++) {
        const r = orientation === 'v' ? row + i : row;
        const c = orientation === 'h' ? col + i : col;
        grid[r][c] = id;
      }
      return { id, row, col, length, orientation, locked: false, isKey: false };
    }
  }
  return null;
}

// BFS with state cap — returns { sol, capped }
// sol = move array if solved, null if unsolvable; capped = true if hit limit before exhausting
function solvePuzzleQuick(initialPieces, maxStates) {
  if (isWon(initialPieces)) return { sol: [], capped: false };
  const queue = [{ pieces: initialPieces, moves: [] }];
  const visited = new Set([encodeState(initialPieces)]);
  while (queue.length > 0) {
    if (visited.size > maxStates) return { sol: null, capped: true };
    const { pieces, moves } = queue.shift();
    for (const move of getValidMoves(pieces)) {
      const next = applyMove(pieces, move);
      const key = encodeState(next);
      if (!visited.has(key)) {
        visited.add(key);
        const nm = [...moves, move];
        if (isWon(next)) return { sol: nm, capped: false };
        queue.push({ pieces: next, moves: nm });
      }
    }
  }
  return { sol: null, capped: false }; // fully explored, unsolvable
}

export function generatePuzzle(difficulty = 'hard') {
  const cfgMap = {
    medium: { minPieces: 6,  maxPieces: 9,  minSol: 6,  quickCap: 8000  },
    hard:   { minPieces: 8,  maxPieces: 12, minSol: 10, quickCap: 8000  },
    expert: { minPieces: 10, maxPieces: 14, minSol: 14, quickCap: 8000  },
  };
  const cfg = cfgMap[difficulty] || cfgMap.hard;

  for (let attempt = 0; attempt < 1000; attempt++) {
    const numOther = cfg.minPieces + Math.floor(Math.random() * (cfg.maxPieces - cfg.minPieces + 1));

    // Key at col 0 or 1 — far from exit so the path is naturally blocked
    const grid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null));
    const keyCol = Math.floor(Math.random() * 2);
    grid[KEY_ROW][keyCol] = 'key';
    grid[KEY_ROW][keyCol + 1] = 'key';
    const pieces = [{
      id: 'key', row: KEY_ROW, col: keyCol, length: 2,
      orientation: 'h', locked: false, isKey: true,
    }];

    for (let i = 0; i < numOther && i < GEN_BLOCK_IDS.length; i++) {
      const p = tryPlacePieceOnGrid(GEN_BLOCK_IDS[i], grid);
      if (p) pieces.push(p);
    }

    if (pieces.length < cfg.minPieces) continue;

    // 1×1 pieces are always locked (can't slide); 10% chance for larger pieces
    for (let i = 1; i < pieces.length; i++) {
      if (pieces[i].length === 1 || Math.random() < 0.10) {
        pieces[i] = { ...pieces[i], locked: true };
      }
    }

    // Pass 1: quick BFS
    const { sol: quick, capped } = solvePuzzleQuick(pieces, cfg.quickCap);

    if (!capped) {
      // State space fully explored (fast) — either solved or unsolvable
      if (quick && quick.length >= cfg.minSol) return pieces;
      continue; // unsolvable or too short — skip immediately
    }

    // Cap was hit — state space is large (hard puzzle) → full BFS
    const full = solvePuzzle(pieces);
    if (full && full.length >= cfg.minSol) return pieces;
  }
  return null;
}

// Pre-built practice puzzles (all verified solvable)
export const PUZZLES = [
  {
    id: 1, name: 'Beginner', description: '2 moves',
    pieces: [
      { id: 'key', row: 3, col: 0, length: 2, orientation: 'h', locked: false, isKey: true },
      { id: 'A', row: 1, col: 2, length: 3, orientation: 'v', locked: false, isKey: false },
    ],
  },
  {
    id: 2, name: 'Easy', description: '3 moves',
    pieces: [
      { id: 'key', row: 3, col: 0, length: 2, orientation: 'h', locked: false, isKey: true },
      { id: 'A', row: 2, col: 2, length: 2, orientation: 'v', locked: false, isKey: false },
      { id: 'B', row: 3, col: 4, length: 2, orientation: 'v', locked: false, isKey: false },
      { id: 'C', row: 1, col: 4, length: 2, orientation: 'h', locked: false, isKey: false },
    ],
  },
  {
    id: 3, name: 'Medium', description: '4 moves',
    pieces: [
      { id: 'key', row: 3, col: 0, length: 2, orientation: 'h', locked: false, isKey: true },
      { id: 'A', row: 1, col: 2, length: 3, orientation: 'v', locked: false, isKey: false },
      { id: 'B', row: 0, col: 1, length: 2, orientation: 'h', locked: false, isKey: false },
      { id: 'D', row: 2, col: 4, length: 2, orientation: 'v', locked: false, isKey: false },
    ],
  },
  {
    id: 4, name: 'Locked Block', description: 'Locked piece challenge',
    pieces: [
      { id: 'key', row: 3, col: 0, length: 2, orientation: 'h', locked: false, isKey: true },
      { id: 'A', row: 2, col: 2, length: 2, orientation: 'v', locked: false, isKey: false },
      { id: 'B', row: 3, col: 3, length: 2, orientation: 'v', locked: false, isKey: false },
      { id: 'X', row: 1, col: 2, length: 1, orientation: 'h', locked: true, isKey: false },
    ],
  },
];
